/** Petites règles d'affichage sur les signatures de contrat — pures. */
import type { SignatureContrat } from './types'
import { chiffresTelephone } from './whatsapp'

/** La signature à montrer : la signée la plus récente, à défaut la plus récente tout court. */
export function signatureCourante(signatures: readonly SignatureContrat[]): SignatureContrat | null {
  const parDate = [...signatures].sort((a, b) => b.creeLe.localeCompare(a.creeLe))
  return parDate.find((s) => s.statut === 'signe') ?? parDate[0] ?? null
}

export const LIBELLE_METHODE: Record<SignatureContrat['methode'], string> = {
  app: "Signature dans l'application",
  documenso: 'Signature électronique (Documenso)',
}

export const LIBELLE_STATUT_SIGNATURE: Record<SignatureContrat['statut'], string> = {
  envoye: 'Envoyé, en attente',
  signe: 'Signé',
  refuse: 'Refusé',
  expire: 'Expiré',
}

export const COULEUR_STATUT_SIGNATURE: Record<SignatureContrat['statut'], string> = {
  envoye: 'yellow',
  signe: 'green',
  refuse: 'red',
  expire: 'gray',
}

/** Un lien de signature Documenso est déjà parti et n'a pas encore abouti. */
export function signatureDocumensoEnCours(signatures: readonly SignatureContrat[]): SignatureContrat | null {
  return signatures.find((s) => s.methode === 'documenso' && s.statut === 'envoye') ?? null
}

export function messageSignature(nom: string, kind: 'livreur' | 'commercant'): string {
  const qui = nom.trim() ? `Bonjour ${nom.trim()}, ` : 'Bonjour, '
  const quoi = kind === 'livreur' ? 'votre contrat de livreur partenaire SwiftEats' : 'votre contrat de partenariat SwiftEats'
  return `${qui}voici le lien pour signer ${quoi} :`
}

/* ------------------------------------------------ canal d'envoi du contrat */

export type MoyenEnvoiContrat = 'email' | 'whatsapp'

export interface OptionsEnvoiContrat {
  email: boolean
  whatsapp: boolean
  /** Le canal proposé d'office : l'e-mail s'il existe, sinon WhatsApp, sinon aucun. */
  defaut: MoyenEnvoiContrat | null
  raisonEmail: string | null
  raisonWhatsapp: string | null
  /** Ni l'un ni l'autre : l'envoi est impossible tant que la fiche n'est pas complétée. */
  blocage: string | null
}

/**
 * Les deux façons de faire parvenir le lien de signature, et pourquoi l'une d'elles est indisponible.
 * WhatsApp : lien `wa.me` ouvert par l'équipe, numéro malien à 8 chiffres accepté (indicatif 223 ajouté).
 * Même règle que la console SwiftDeliver (`optionsEnvoiContrat` des dossiers PME).
 */
export function optionsEnvoiContrat(
  c: { email: string | null | undefined; telephone: string | null | undefined },
  qui: 'livreur' | 'commercant',
): OptionsEnvoiContrat {
  const fiche = qui === 'livreur' ? 'La fiche du livreur' : 'La fiche du gérant'
  const email = (c.email ?? '').trim().length > 0
  const brut = (c.telephone ?? '').trim()
  const whatsapp = chiffresTelephone(brut, '223') !== null
  const raisonEmail = email ? null : `${fiche} n'a pas d'adresse e-mail.`
  const raisonWhatsapp = whatsapp
    ? null
    : brut
      ? `Le numéro « ${brut} » est illisible : précisez l'indicatif du pays (ex. +223 75 08 36 64).`
      : `${fiche} n'a pas de numéro de téléphone.`
  return {
    email,
    whatsapp,
    defaut: email ? 'email' : whatsapp ? 'whatsapp' : null,
    raisonEmail,
    raisonWhatsapp,
    blocage: email || whatsapp
      ? null
      : `${fiche} n'a ni adresse e-mail ni numéro WhatsApp exploitable : renseignez l'un des deux avant d'envoyer le contrat.`,
  }
}
